/**
 * Guide content for major version 2 (taksoai-app@2.x.x).
 *
 * Authoring rules:
 *  - Keep `id` values stable — they are safe to use as analytics event names.
 *  - `target` must match a `data-onboarding-id="..."` attribute in the DOM.
 *  - `placement: 'auto'` lets the renderer pick the side with the most space.
 *  - `routeScope` gates the step to a specific page; the guide pauses at scope
 *    boundaries and resumes automatically when the user navigates there.
 *  - Leave `media` undefined until assets are ready; the renderer ignores it safely.
 *
 * To add a new version guide, create steps-v27.ts (major+minor) and register it in index.ts.
 */

import type { GuideStep } from './types';

export const GUIDE_STEPS_V2: GuideStep[] = [
  // ─── Step 0 — Welcome ──────────────────────────────────────────────────────
  {
    id: 'welcome',
    index: 0,
    type: 'modal',
    routeScope: 'home',
    hideBack: true,
    title: 'Welcome to TaksoAI',
    description:
      'TaksoAI helps you analyze blueprints for HVAC, plumbing, and piping systems. ' +
      'This short guide walks you through the complete estimation workflow — ' +
      'from creating a project all the way to exporting your results. ' +
      'You can skip at any time and restart from the "Get Started" button in the header.',
  },

  // ─── Step 1 — Search Projects ──────────────────────────────────────────────
  {
    id: 'search-projects',
    index: 1,
    type: 'spotlight',
    routeScope: 'home',
    title: 'Search Your Projects',
    description:
      'Use the search bar to quickly find any project by name. Start typing and matching results appear instantly — no need to scroll through the full list.',
    target: 'search-projects',
    placement: 'bottom',
  },

  // ─── Step 2 — Select Project ───────────────────────────────────────────────
  {
    id: 'select-project',
    index: 2,
    type: 'spotlight',
    routeScope: 'home',
    title: 'Select Projects',
    description:
      'Tick the checkbox on a project card or table row to select it. You can select multiple projects at once — a bulk-action toolbar will appear at the top of the list.',
    target: 'project-row-box',
    placement: 'bottom',
  },

  // ─── Step 3 — Delete Project ───────────────────────────────────────────────
  {
    id: 'delete-project',
    index: 3,
    type: 'spotlight',
    routeScope: 'home',
    title: 'Delete a Project',
    description:
      'With one or more projects selected, use the action toolbar to delete or archive them. Deleted projects are permanently removed, so double-check your selection before confirming.',
    target: 'project-tools',
    placement: 'bottom',
  },

  // ─── Step 4 — Create Project ───────────────────────────────────────────────
  {
    id: 'create-project',
    index: 4,
    type: 'spotlight',
    routeScope: 'home',
    title: 'Create a New Project',
    description:
      'Click "Add A Project" to start a new estimation job. A dialog will guide you through naming the project and setting its location and construction type.',
    target: 'create-project-btn',
    placement: 'left',
  },

  // ─── Step 5 — Bridge: go to project detail ─────────────────────────────────
  {
    id: 'goto-project-page',
    index: 5,
    type: 'spotlight',
    routeScope: 'home',
    title: 'Open a Project to Continue',
    description:
      'Click on any project card to open the project detail page. The guide will pick up right where you left off — showing you how to upload drawings and configure AI settings.',
    target: 'project-card',
    placement: 'bottom',
    hideNext: true,
  },

  // ─── Step 6 — Add Details ──────────────────────────────────────────────────
  {
    id: 'add-details',
    index: 6,
    type: 'spotlight',
    routeScope: 'project-detail',
    hideBack: true,
    title: 'Add Project Details',
    description:
      'Fill in project metadata such as construction sector, project stage, and location. ' +
      'This information keeps records searchable and allows you to get more insight into your projects.',
    target: 'add-details',
    placement: 'right',
  },

  // ─── Step 7 — Equipment Settings ──────────────────────────────────────────
  {
    id: 'equipment-settings',
    index: 7,
    type: 'spotlight',
    routeScope: 'project-detail',
    title: 'Equipment Settings',
    description:
      'Configure your equipment catalogue before running AI analysis. Set system type defaults, units, and the equipment categories the AI should detect.',
    target: 'equipment-settings',
    placement: 'bottom',
  },

  // ─── Step 8 — Upload Drawings ──────────────────────────────────────────────
  {
    id: 'upload-drawings',
    index: 8,
    type: 'spotlight',
    routeScope: 'project-detail',
    title: 'Upload Your Drawings',
    description:
      'Upload one or more blueprint PDF files. TaksoAI processes each page as a separate drawing canvas, so multi-page PDFs are fully supported.',
    target: 'upload-drawings',
    placement: 'bottom',
  },

  // ─── Step 9 — AI Module Selection ─────────────────────────────────────────
  {
    id: 'ai-module-selection',
    index: 9,
    type: 'spotlight',
    routeScope: 'project-detail',
    title: 'AI Module Selection',
    description:
      'Choose which AI modules to run on your uploaded drawings. Each module targets a specific system type — select all that apply to your project.',
    target: 'ai-module-selection',
    placement: 'bottom',
  },

  // ─── Step 10 — AI Parameters Input ────────────────────────────────────────
  {
    id: 'ai-params-input',
    index: 10,
    type: 'spotlight',
    routeScope: 'project-detail',
    title: 'Set Scale & Heights',
    description:
      'Enter the measurement inputs required to run the algorithm — such as floor-to-floor height and scale. These parameters directly affect the accuracy of length and quantity estimates.',
    target: 'ai-params-input',
    placement: 'bottom',
  },

  // ─── Step 11 — Start AI Job ────────────────────────────────────────────────
  {
    id: 'start-ai-job',
    index: 11,
    type: 'spotlight',
    routeScope: 'project-detail',
    title: 'Start AI Job',
    description:
      'Select one or more drawing pages then click Run to launch the AI analysis. TaksoAI will process each page and automatically detect equipment, ducts, and pipes.',
    target: 'start-ai-job',
    placement: 'left',
  },

  // ─── Step 12 — Bridge: go to drawing ──────────────────────────────────────
  {
    id: 'goto-drawing-page',
    index: 12,
    type: 'spotlight',
    routeScope: 'project-detail',
    title: 'Open a Drawing to Continue',
    description:
      'Click on any drawing card with green check mark(AI job is done) in the gallery to open the blueprint editor. ' +
      'The guide will continue there with the full set of drawing and analysis tools.',
    target: 'drawing-card',
    placement: 'bottom',
    hideNext: true,
  },

  // ─── Step 13 — Page Thumbnails ────────────────────────────────────────────
  {
    id: 'page-thumbnails',
    index: 13,
    hideBack: true,
    type: 'spotlight',
    routeScope: 'drawing',
    title: 'Page Thumbnails',
    description:
      'Browse and switch between drawing pages using the thumbnail panel on the left. Each thumbnail shows a miniature preview of the page — click one to jump directly to it.',
    target: 'page-thumbnails',
    placement: 'right',
  },

  // ─── Step 14 — Map Tools ──────────────────────────────────────────────────
  {
    id: 'map-control',
    index: 14,
    type: 'spotlight',
    routeScope: 'drawing',
    title: 'Map Tools',
    description:
      'Use the toolbar to measure distances with the ruler, clear your current selection, apply the magic brush for bulk operations, snip pipe segments, or draw insulation polygons.',
    target: 'map-control',
    placement: 'bottom',
  },

  // ─── Step 15 — Filter Tools ───────────────────────────────────────────────
  {
    id: 'filter-symbol',
    index: 15,
    type: 'spotlight',
    routeScope: 'drawing',
    title: 'Filter Symbols',
    description:
      'Narrow the visible symbols by type or system using the filter dropdowns. Enable Inverse Filtering to highlight everything except the selected category.',
    target: 'filter-symbol',
    placement: 'bottom',
  },

  // ─── Step 16 — Size Filter ────────────────────────────────────────────────
  {
    id: 'size-filter',
    index: 16,
    type: 'spotlight',
    routeScope: 'drawing',
    title: 'Size Filter',
    description:
      'Filter the drawing by duct or pipe size. Pick a specific rectangular, round, or pipe dimension from the dropdown to focus only on symbols of that size.',
    target: 'size-filter-section',
    placement: 'bottom',
  },

  // ─── Step 17 — Add New Elements ───────────────────────────────────────────
  {
    id: 'add-new-element',
    index: 17,
    type: 'spotlight',
    routeScope: 'drawing',
    title: 'Add New Symbols',
    description:
      'Place duct fittings, pipe fittings, plumbing symbols, duct/pipe run lengths, or equipment directly onto the drawing. Select the tool, choose a subtype from the dropdown, then click on the blueprint to position it.',
    target: 'add-new-element',
    placement: 'bottom',
  },

  // ─── Step 18 — Single Editing ─────────────────────────────────────────────
  {
    id: 'single-editing',
    index: 18,
    type: 'spotlight',
    routeScope: 'drawing',
    title: 'Single Symbol Editing',
    description:
      "Click on any symbol in the drawing to select it. A properties panel will slide in from the left, letting you inspect and adjust the symbol's type, size, system, and other attributes. " +
      'Confirm your changes to update the symbol and change it to green, meaning it has been reviewed.',
    target: 'single-editing',
    placement: 'right',
  },

  // ─── Step 19 — Batch Editing ──────────────────────────────────────────────
  {
    id: 'batch-editing',
    index: 19,
    type: 'spotlight',
    routeScope: 'drawing',
    title: 'Batch Editing',
    description:
      'Hold control to select multiple symbols with drag box at once to edit their properties in bulk. ' +
      'Change system type, size, or other attributes across all selected items simultaneously.',
    target: 'batch-editing',
    placement: 'right',
  },

  // ─── Step 20 — Remove Errors ──────────────────────────────────────────────
  {
    id: 'remove-errors',
    index: 20,
    type: 'spotlight',
    routeScope: 'drawing',
    title: 'Remove False Positives',
    description:
      'The AI may occasionally flag symbols that are not actual equipment. Review and remove false positives here to keep your quantities accurate.',
    target: 'remove-errors',
    placement: 'right',
  },

  // ─── Step 21 — Review Panel ───────────────────────────────────────────────
  {
    id: 'review-panel',
    index: 21,
    type: 'spotlight',
    routeScope: 'drawing',
    title: 'Review Panel',
    description:
      'Open the Review Panel to see a structured summary of all detected and manually added items. Check quantities, run lengths, and system breakdowns before exporting.',
    target: 'review-panel-openner',
    placement: 'left',
  },

  // ─── Step 22 — Export ─────────────────────────────────────────────────────
  {
    id: 'export',
    index: 22,
    type: 'spotlight',
    routeScope: 'drawing',
    title: 'Export Your Results',
    description:
      'Export your estimation to Excel or PDF. The report includes itemised quantities, run lengths, and system totals across all pages in your project.',
    target: 'export-panel-openner',
    placement: 'bottom',
  },

  // ─── Step 23 — Success ────────────────────────────────────────────────────
  {
    id: 'success',
    index: 23,
    type: 'modal',
    // No routeScope — success modal appears on whatever page the user completes the guide.
    title: "You're All Set!",
    description:
      "You've completed the TaksoAI onboarding guide. " +
      'You can replay it at any time by clicking the "Get Started" button in the header. ' +
      'For deeper reference, the full User Guide is always available via the book icon. Happy estimating!',
  },
];
